import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Input, Select, message,
  Card, Tag, Space, Popconfirm, Upload, Image, Tabs, List, Switch
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, EyeOutlined, UploadOutlined,
  HistoryOutlined, LinkOutlined, ShareAltOutlined, SearchOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import api, { getImageUrl } from '../utils/api';

const { TabPane } = Tabs;
const { Option } = Select;
const { TextArea } = Input;

const Materials = () => {
  const [materials, setMaterials] = useState([]);
  const [bms, setBms] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [uploadRecords, setUploadRecords] = useState([]);
  const [selectedBm, setSelectedBm] = useState(null);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [batchFileList, setBatchFileList] = useState([]);
  const [batchUploadModalVisible, setBatchUploadModalVisible] = useState(false);
  const [batchUploadProgress, setBatchUploadProgress] = useState(0);
  const [batchUploadResults, setBatchUploadResults] = useState(null);
  const [batchUploading, setBatchUploading] = useState(false);
  const [failedFiles, setFailedFiles] = useState([]); // 记录上传失败的文件
  const [form] = Form.useForm();
  const [batchForm] = Form.useForm();
  
  const [filterForm] = Form.useForm();
  const [activeFilters, setActiveFilters] = useState({ name: '', hash: '' });

  // 获取素材列表
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });

  const fetchMaterials = async (page = 1, pageSize = 20, filters = null) => {
    setLoading(true);
    const search = filters ?? activeFilters;
    try {
      const params = { page, page_size: pageSize };
      if (search.name?.trim()) params.name = search.name.trim();
      if (search.hash?.trim()) params.hash = search.hash.trim();
      const response = await api.get('/materials/', { params });
      setMaterials(response.data.items);
      setPagination({
        current: page,
        pageSize: pageSize,
        total: response.data.total
      });
    } catch (error) {
      message.error('获取素材列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取 BM 列表
  const fetchBMs = async () => {
    try {
      const response = await api.get('/business-managers/');
      setBms(response.data);
      console.log('Loaded BMs:', response.data);
      if (response.data.length === 0) {
        message.warning('BM列表为空，请先添加BM');
      }
    } catch (error) {
      console.error('Failed to load BMs:', error);
      message.error('获取 BM 列表失败: ' + (error.response?.data?.detail || error.message));
    }
  };

  // 获取广告账户列表（根据BM ID从数据库获取）
  const fetchAccounts = async (bmId, associatedAccountIds = null) => {
    try {
      console.log('Fetching accounts for BM:', bmId, 'Associated IDs:', associatedAccountIds);
      const response = await api.get(`/business-managers/${bmId}/assets`);
      console.log('Accounts response:', response.data);
      // 只返回该BM下的广告账户
      let bmAccounts = response.data.ad_accounts || [];
      
      // 如果指定了关联的账户ID，只显示这些账户
      if (associatedAccountIds && associatedAccountIds.length > 0) {
        bmAccounts = bmAccounts.filter(account => associatedAccountIds.includes(account.id));
        console.log(`Filtered to ${bmAccounts.length} associated accounts out of ${response.data.ad_accounts?.length || 0} total`);
      }
      
      setAccounts(bmAccounts);
      console.log(`Loaded ${bmAccounts.length} accounts for BM ${bmId}`);
      if (bmAccounts.length === 0) {
        message.info('该素材没有关联的广告账户');
      }
    } catch (error) {
      console.error('Failed to load accounts:', error);
      message.error('获取广告账户失败: ' + (error.response?.data?.detail || error.message));
      setAccounts([]);
    }
  };

  useEffect(() => {
    fetchMaterials();
    fetchBMs();
  }, []);

  // 上传素材
  const handleUpload = async (values) => {
    if (fileList.length === 0) {
      message.error('请选择文件');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', fileList[0]);
      formData.append('name', values.name);
      formData.append('bm_id', values.bm_id);
      formData.append('category', values.category || 'uncategorized');
      
      values.ad_account_ids.forEach(id => {
        formData.append('ad_account_ids', id);
      });

      console.log('Uploading with data:', {
        name: values.name,
        bm_id: values.bm_id,
        ad_account_ids: values.ad_account_ids,
        category: values.category || 'uncategorized',
        file: fileList[0].name
      });

      const response = await api.post('/materials/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('Upload response:', response.data);
      
      // 显示Facebook上传结果
      const fbResults = response.data.facebook_upload_results || [];
      const successCount = fbResults.filter(r => r.status === 'success').length;
      const failCount = fbResults.filter(r => r.status === 'failed').length;
      const skipCount = fbResults.filter(r => r.status === 'skipped').length;

      if (response.data.message) {
        if (failCount > 0) {
          message.warning(response.data.message);
        } else {
          message.info(response.data.message);
        }
      } else if (fbResults.length > 0) {
        if (failCount === 0) {
          message.success(
            `上传成功！已上传 ${successCount} 个账户` +
            (skipCount ? `，跳过 ${skipCount} 个` : '')
          );
        } else {
          message.warning(`素材保存成功！Facebook上传: 成功${successCount}个, 失败${failCount}个`);
          fbResults.filter(r => r.status === 'failed').forEach(r => {
            message.error(`账户 ${r.ad_account_id}: ${r.error}`);
          });
        }
      } else {
        message.success('素材上传成功');
      }
      
      setUploadModalVisible(false);
      setFileList([]);
      form.resetFields();
      fetchMaterials();
    } catch (error) {
      console.error('Upload error:', error);
      console.error('Error response:', error.response?.data);
      // 显示详细的验证错误
      if (error.response?.data?.detail && Array.isArray(error.response.data.detail)) {
        const errors = error.response.data.detail.map(err => {
          if (typeof err === 'object') {
            return `${err.loc?.join('.')}: ${err.msg}`;
          }
          return err;
        }).join('\n');
        message.error('上传失败:\n' + errors);
      } else if (error.response?.data?.detail) {
        message.error('上传失败: ' + JSON.stringify(error.response.data.detail));
      } else {
        message.error('上传失败');
      }
    }
  };

  // 批量上传素材
  const handleBatchUpload = async (values, isRetry = false, retryFiles = null) => {
    // 防止重复调用
    if (batchUploading && !isRetry) {
      console.warn('Batch upload already in progress, ignoring duplicate call');
      return;
    }
    
    const filesToUpload = retryFiles || batchFileList;
    
    if (filesToUpload.length === 0) {
      message.error('请选择文件');
      return;
    }

    try {
      setBatchUploading(true);
      setBatchUploadProgress(0);
      if (!isRetry) {
        setBatchUploadResults(null);
        setFailedFiles([]);
      }
      
      const formData = new FormData();
      filesToUpload.forEach((file, index) => {
        formData.append('files', file);
      });
      formData.append('bm_id', values.bm_id);
      formData.append('category', values.category || 'uncategorized');
      formData.append('duplicate_strategy', values.duplicate_strategy || 'skip');
      formData.append('create_collection', values.create_collection ? 'true' : 'false');
      if (values.create_collection && values.collection_name) {
        formData.append('collection_name', values.collection_name);
        if (values.collection_description) {
          formData.append('collection_description', values.collection_description);
        }
      }
      
      values.ad_account_ids.forEach(id => {
        formData.append('ad_account_ids', id);
      });

      const response = await api.post('/materials/batch-upload-v2', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setBatchUploadProgress(percentCompleted);
        }
      });

      console.log('Batch upload v2 response:', response.data);
      
      // 如果是重试，合并结果
      if (isRetry && batchUploadResults) {
        const newResults = response.data;
        setBatchUploadResults({
          ...newResults,
          success_count: (batchUploadResults.success_count || 0) + (newResults.success_count || 0),
          failed_count: newResults.failed_count || 0,
          skipped_count: (batchUploadResults.skipped_count || 0) + (newResults.skipped_count || 0),
          results: [...(batchUploadResults.results || []), ...(newResults.results || [])]
        });
      } else {
        setBatchUploadResults(response.data);
      }
      
      // 记录失败的文件用于重试
      if (response.data.results) {
        const failed = response.data.results
          .filter(r => r.status === 'failed')
          .map(r => {
            // 找到对应的文件对象
            return filesToUpload.find(f => f.name === r.filename) || { name: r.filename };
          });
        setFailedFiles(failed);
      }
      
      const successCount = response.data.success_count || 0;
      const failedCount = response.data.failed_count || 0;
      const skippedCount = response.data.skipped_count || 0;
      const elapsedTime = response.data.elapsed_time_seconds || 0;
      
      if (failedCount === 0 && skippedCount === 0) {
        message.success(`批量上传成功！共上传 ${successCount} 个素材，耗时 ${elapsedTime}秒`);
        setFailedFiles([]);
      } else {
        message.warning(
          `批量上传完成！成功: ${successCount}, 失败: ${failedCount}, 跳过: ${skippedCount}, 耗时: ${elapsedTime}秒`
        );
      }
      
      fetchMaterials();
      setBatchUploading(false);
    } catch (error) {
      console.error('Batch upload error:', error);
      message.error('批量上传失败: ' + (error.response?.data?.detail || error.message));
      setBatchUploading(false);
    }
  };

  // 重新上传失败的文件
  const handleRetryFailed = async () => {
    if (failedFiles.length === 0) {
      message.info('没有需要重新上传的文件');
      return;
    }
    
    const values = batchForm.getFieldsValue();
    message.info(`正在重新上传 ${failedFiles.length} 个失败的文件...`);
    await handleBatchUpload(values, true, failedFiles);
  };

  // 分享素材到其他Facebook广告账户
  const handleShareMaterial = async (materialId, accountIds) => {
    try {
      message.loading('正在分享到 Facebook...', 0);
      const response = await api.post(
        `/materials/${materialId}/share`,
        { ad_account_ids: accountIds }
      );
      message.destroy();
      
      // 显示详细结果
      const results = response.data.upload_results || [];
      const successCount = results.filter(r => r.status === 'success').length;
      const failCount = results.filter(r => r.status === 'failed').length;
      
      if (failCount === 0) {
        message.success(`成功分享到 ${successCount} 个广告账户`);
      } else {
        message.warning(`成功: ${successCount}, 失败: ${failCount}`);
        // 显示失败详情
        results.filter(r => r.status === 'failed').forEach(r => {
          message.error(`账户 ${r.ad_account_id}: ${r.error}`);
        });
      }
      
      fetchUploadRecords(materialId);
      fetchMaterials(); // 刷新素材列表
    } catch (error) {
      message.destroy();
      message.error('上传失败: ' + (error.response?.data?.detail || error.message));
    }
  };

  // 获取上传记录
  const fetchUploadRecords = async (materialId) => {
    try {
      const response = await api.get(`/materials/${materialId}/upload-records`);
      setUploadRecords(response.data);
    } catch (error) {
      message.error('获取上传记录失败');
    }
  };

  // 删除素材
  const handleDelete = async (id) => {
    try {
      await api.delete(`/materials/${id}`);
      message.success('删除成功');
      fetchMaterials();
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 查看素材详情
  const handleViewDetail = (material) => {
    setSelectedMaterial(material);
    fetchUploadRecords(material.id);
    setDetailVisible(true);
  };

  const isVideoMaterial = (material) => {
    const mime = (material?.mime_type || '').toLowerCase();
    if (mime.startsWith('video/')) return true;
    const url = material?.file_url || material?.name || '';
    return /\.(mp4|mov|avi|webm|mkv|m4v|mpeg|mpg)$/i.test(url);
  };

  const ACCEPT_MEDIA =
    'image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/webm,video/x-msvideo,video/x-matroska,.mp4,.mov,.avi,.webm,.mkv,.m4v';

  const validateMediaFile = (file) => {
    const mime = (file.type || '').toLowerCase();
    const isImage = mime.startsWith('image/') || /\.(jpe?g|png|gif|webp|bmp)$/i.test(file.name);
    const isVideo = mime.startsWith('video/') || /\.(mp4|mov|avi|webm|mkv|m4v|mpeg|mpg)$/i.test(file.name);
    if (!isImage && !isVideo) {
      message.error(`不支持的文件类型: ${file.name}`);
      return false;
    }
    const maxBytes = 100 * 1024 * 1024;
    if (file.size > maxBytes) {
      message.error(`${file.name} 超过 100MB 限制`);
      return false;
    }
    return true;
  };

  const columns = [
    {
      title: '预览',
      dataIndex: 'file_url',
      key: 'preview',
      render: (url, record) => (
        isVideoMaterial(record) ? (
          <video
            src={getImageUrl(url)}
            style={{ width: 80, height: 80, objectFit: 'cover', background: '#111' }}
            muted
            playsInline
          />
        ) : (
          <Image
            src={getImageUrl(url)}
            alt="素材预览"
            style={{ width: 80, height: 80, objectFit: 'cover' }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg=="
          />
        )
      ),
    },
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Space>
          <span>{name}</span>
          {isVideoMaterial(record) && <Tag color="blue">视频</Tag>}
        </Space>
      ),
    },
    {
      title: 'Hash / Video ID',
      dataIndex: 'facebook_hash',
      key: 'facebook_hash',
      render: (hash, record) => (
        <span>
          {hash ? (
            <Tag>{hash}</Tag>
          ) : (
            <Tag color="orange">{record.file_hash || '-'}</Tag>
          )}
        </span>
      ),
    },
    {
      title: 'BM',
      dataIndex: 'bm_id',
      key: 'bm_id',
      render: (bmId) => {
        const bm = bms.find(b => b.id === bmId);
        if (!bm) return '-';
        return `${bm.name} (ID: ${bm.bm_id})`;
      },
    },
    {
      title: '关联账户',
      dataIndex: 'ad_accounts',
      key: 'ad_accounts',
      render: (accounts) => (
        <span>{accounts?.length || 0} 个账户</span>
      ),
    },
    {
      title: '使用次数',
      dataIndex: 'usage_count',
      key: 'usage_count',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        // 如果没有 facebook_hash，显示上传失败状态
        if (!record.facebook_hash) {
          return <Tag color="red">上传失败</Tag>;
        }
        return (
          <Tag color={status === 'active' ? 'green' : 'red'}>
            {status === 'active' ? '正常' : '停用'}
          </Tag>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          {record.facebook_hash && record.facebook_hash !== 'null' && record.facebook_hash !== 'undefined' ? (
            // 如果有 facebook_hash，显示详情按钮
            <Button
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record)}
            >
              详情
            </Button>
          ) : (
            // 如果没有 facebook_hash，显示上传按钮
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={() => {
                setSelectedMaterial(record);
                // 设置BM和默认广告账户
                form.setFieldsValue({
                  bm_id: record.bm_id,
                  ad_account_ids: record.ad_accounts?.map(acc => acc.id) || []
                });
                setSelectedBm(record.bm_id);
                // 加载该BM下的广告账户
                if (record.bm_id) {
                  fetchAccounts(record.bm_id);
                }
                setUploadModalVisible(true);
              }}
            >
              上传
            </Button>
          )}
          <Button
            icon={<ShareAltOutlined />}
            onClick={() => {
              setSelectedMaterial(record);
              // 只设置BM，不预选广告账户，让用户手动选择
              form.setFieldsValue({
                bm_id: record.bm_id,
                ad_account_ids: []  // 清空广告账户选择
              });
              setSelectedBm(record.bm_id);
              // 加载该BM下的所有广告账户（不限制只显示关联账户）
              if (record.bm_id) {
                fetchAccounts(record.bm_id);
              }
              setUploadModalVisible(true);
            }}
          >
            分享
          </Button>
          <Popconfirm
            title="确定删除吗？"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button icon={<DeleteOutlined />} danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleSearch = () => {
    const values = filterForm.getFieldsValue();
    const filters = { name: values.name || '', hash: values.hash || '' };
    setActiveFilters(filters);
    fetchMaterials(1, pagination.pageSize, filters);
  };

  const handleReset = () => {
    filterForm.resetFields();
    const filters = { name: '', hash: '' };
    setActiveFilters(filters);
    fetchMaterials(1, pagination.pageSize, filters);
  };

  return (
    <div>
      <Card
        title="素材管理"
        extra={
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setSelectedMaterial(null);
                setFileList([]);
                form.resetFields();
                setUploadModalVisible(true);
              }}
            >
              上传素材
            </Button>
            <Button
              type="default"
              icon={<UploadOutlined />}
              onClick={() => {
                setBatchFileList([]);
                setBatchUploadModalVisible(true);
              }}
            >
              批量上传
            </Button>
          </Space>
        }
      >
        <Form
          form={filterForm}
          layout="inline"
          onFinish={handleSearch}
          style={{ marginBottom: 16, flexWrap: 'wrap' }}
        >
          <Form.Item label="名称" name="name">
            <Input placeholder="素材名称" allowClear style={{ width: 200 }} />
          </Form.Item>
          <Form.Item label="Hash" name="hash">
            <Input placeholder="Facebook Hash" allowClear style={{ width: 280 }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} htmlType="submit">
                查询
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
        <Table
          columns={columns}
          dataSource={materials}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            onChange: (page, pageSize) => fetchMaterials(page, pageSize),
            onShowSizeChange: (page, pageSize) => fetchMaterials(page, pageSize)
          }}
        />
      </Card>

      {/* 上传模态框 */}
      <Modal
        title={selectedMaterial ? '分享到 Facebook' : '上传新素材'}
        open={uploadModalVisible}
        onOk={() => form.submit()}
        onCancel={() => {
          setUploadModalVisible(false);
          setFileList([]);
          form.resetFields();
        }}
        width={600}
      >
        <Form
          form={form}
          onFinish={(values) => {
            if (selectedMaterial) {
              // 上传到 Facebook
              handleShareMaterial(selectedMaterial.id, values.ad_account_ids);
              setUploadModalVisible(false);
              form.resetFields();
            } else {
              // 上传新素材到服务器
              handleUpload(values);
            }
          }}
          layout="vertical"
        >
          {!selectedMaterial && (
            <>
              <Form.Item
                name="file"
                label="选择文件"
                rules={[{ required: true, message: '请选择文件' }]}
              >
                <Upload
                  beforeUpload={(file) => {
                    if (!validateMediaFile(file)) {
                      return Upload.LIST_IGNORE;
                    }
                    setFileList([file]);
                    // 自动填充素材名称为文件名（不含扩展名）
                    const fileName = file.name.replace(/\.[^/.]+$/, "");
                    form.setFieldsValue({ name: fileName });
                    return false;
                  }}
                  onRemove={() => {
                    setFileList([]);
                    form.setFieldsValue({ name: undefined });
                  }}
                  fileList={fileList}
                  maxCount={1}
                  accept={ACCEPT_MEDIA}
                >
                  <Button icon={<UploadOutlined />}>选择图片或视频</Button>
                </Upload>
              </Form.Item>
              <Form.Item
                name="name"
                label="素材名称"
                rules={[{ required: true, message: '请输入素材名称' }]}
              >
                <Input placeholder="上传文件后自动填充文件名" />
              </Form.Item>
            </>
          )}
          
          <Form.Item
            name="bm_id"
            label="选择 BM"
            rules={[{ required: true, message: '请选择 BM' }]}
          >
            <Select
              placeholder="选择 BM"
              onChange={(value) => {
                setSelectedBm(value);
                // 清空已选择的广告账户
                form.setFieldsValue({ ad_account_ids: [] });
                setSelectedAccounts([]);
                // 加载该BM下的广告账户
                if (value) {
                  fetchAccounts(value);
                } else {
                  setAccounts([]);
                }
              }}
            >
              {bms.map(bm => (
                <Option key={bm.id} value={bm.id}>
                  {bm.name} (ID: {bm.bm_id})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="ad_account_ids"
            label="选择广告账户（可多选）"
            rules={[{ required: true, message: '请选择广告账户' }]}
          >
            <Select
              mode="multiple"
              placeholder={selectedBm ? "选择广告账户" : "请先选择BM"}
              disabled={!selectedBm || accounts.length === 0}
              onChange={(values) => setSelectedAccounts(values)}
            >
              {accounts.map(account => (
                <Option key={account.id} value={account.id}>
                  {account.name || `账户 ${account.account_id}`} (ID: {account.account_id})
                </Option>
              ))}
            </Select>
          </Form.Item>

          {!selectedMaterial && (
            <Form.Item
              name="category"
              label="分类"
              initialValue="uncategorized"
            >
              <Select>
                <Option value="uncategorized">未分类</Option>
                <Option value="product">产品</Option>
                <Option value="lifestyle">生活方式</Option>
                <Option value="promotion">促销</Option>
                <Option value="brand">品牌</Option>
              </Select>
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* 批量上传模态框 */}
      <Modal
        title="批量上传素材"
        open={batchUploadModalVisible}
        onOk={() => {
          if (batchUploadResults) {
            // 上传成功后，关闭模态框
            setBatchUploadModalVisible(false);
            setBatchFileList([]);
            setBatchUploadProgress(0);
            setBatchUploadResults(null);
            setBatchUploading(false);
            setFailedFiles([]);
            batchForm.resetFields();
          } else {
            batchForm.submit();
          }
        }}
        onCancel={() => {
          if (!batchUploading) {
            setBatchUploadModalVisible(false);
            setBatchFileList([]);
            setBatchUploadProgress(0);
            setBatchUploadResults(null);
            setBatchUploading(false);
            setFailedFiles([]);
            batchForm.resetFields();
          }
        }}
        width={600}
        okText={batchUploadResults ? '关闭' : (batchUploading ? '上传中...' : '开始上传')}
        confirmLoading={batchUploading}
        cancelButtonProps={{ disabled: batchUploading }}
      >
        <Form
          form={batchForm}
          onFinish={handleBatchUpload}
          layout="vertical"
        >
          <Form.Item
            label="选择文件（可多选）"
            rules={[{ required: true, message: '请选择文件' }]}
          >
            <Upload
              beforeUpload={(file) => {
                if (!validateMediaFile(file)) {
                  return Upload.LIST_IGNORE;
                }
                setBatchFileList(prev => [...prev, file]);
                return false;
              }}
              onRemove={(file) => {
                setBatchFileList(prev => prev.filter(f => f.uid !== file.uid));
              }}
              fileList={batchFileList}
              multiple
              accept={ACCEPT_MEDIA}
            >
              <Button icon={<UploadOutlined />}>选择图片或视频</Button>
            </Upload>
          </Form.Item>
          
          {batchFileList.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <p>已选择 {batchFileList.length} 个文件：</p>
              <List
                dataSource={batchFileList}
                renderItem={(file, index) => (
                  <List.Item key={index}>
                    {file.name} ({(file.size / 1024).toFixed(1)} KB)
                  </List.Item>
                )}
              />
            </div>
          )}

          <Form.Item
            name="bm_id"
            label="选择 BM"
            rules={[{ required: true, message: '请选择 BM' }]}
          >
            <Select
              placeholder="选择 BM"
              onChange={(value) => {
                // 清空已选择的广告账户
                batchForm.setFieldsValue({ ad_account_ids: [] });
                setSelectedAccounts([]);
                // 加载该BM下的广告账户
                if (value) {
                  fetchAccounts(value);
                } else {
                  setAccounts([]);
                }
              }}
            >
              {bms.map(bm => (
                <Option key={bm.id} value={bm.id}>
                  {bm.name} (ID: {bm.bm_id})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="ad_account_ids"
            label="选择广告账户（可多选）"
            rules={[{ required: true, message: '请选择广告账户' }]}
          >
            <Select
              mode="multiple"
              placeholder="选择广告账户"
              disabled={!batchForm.getFieldValue('bm_id') || accounts.length === 0}
            >
              {accounts.map(account => (
                <Option key={account.id} value={account.id}>
                  {account.name || `账户 ${account.account_id}`} (ID: {account.account_id})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="category"
            label="分类"
            initialValue="uncategorized"
          >
            <Select>
              <Option value="uncategorized">未分类</Option>
              <Option value="product">产品</Option>
              <Option value="lifestyle">生活方式</Option>
              <Option value="promotion">促销</Option>
              <Option value="brand">品牌</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="duplicate_strategy"
            label="重复处理策略"
            initialValue="skip"
          >
            <Select>
              <Option value="error">报错（不创建）</Option>
              <Option value="skip">跳过（关联已存在的）</Option>
            </Select>
          </Form.Item>

          <Form.Item name="create_collection" label="创建素材集" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item
            name="collection_name"
            label="素材集名称"
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const createCollection = getFieldValue('create_collection');
                  const isChecked = createCollection === true || createCollection === 'true';
                  if (isChecked && !value) {
                    return Promise.reject(new Error('请输入素材集名称'));
                  }
                  return Promise.resolve();
                }
              })
            ]}
          >
            <Input 
              placeholder="请输入素材集名称" 
              disabled={!(batchForm.getFieldValue('create_collection') === true || batchForm.getFieldValue('create_collection') === 'true')} 
            />
          </Form.Item>

          <Form.Item name="collection_description" label="素材集描述">
            <TextArea 
              placeholder="请输入素材集描述（可选）" 
              rows={3}
              disabled={!(batchForm.getFieldValue('create_collection') === true || batchForm.getFieldValue('create_collection') === 'true')}
            />
          </Form.Item>

          {batchUploadProgress > 0 && (
            <div style={{ marginTop: 16 }}>
              <p>上传进度: {batchUploadProgress}%</p>
              <div style={{ width: '100%', height: 10, backgroundColor: '#f0f0f0', borderRadius: 5 }}>
                <div 
                  style={{ 
                    width: `${batchUploadProgress}%`, 
                    height: '100%', 
                    backgroundColor: '#1890ff',
                    borderRadius: 5
                  }} 
                />
              </div>
            </div>
          )}

          {batchUploadResults && (
            <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
              <p>上传结果：成功 {batchUploadResults.success_count} 个，跳过 {batchUploadResults.skipped_count || 0} 个，失败 {batchUploadResults.failed_count} 个</p>
              
              {batchUploadResults.collection && (
                <div style={{ marginTop: 8, padding: 8, backgroundColor: '#e6f7ff', borderRadius: 4 }}>
                  <p style={{ color: '#1890ff', fontWeight: 'bold' }}>
                    ✅ 素材集 {'"'+batchUploadResults.collection.name+'"'} 创建成功！
                  </p>
                  <p style={{ fontSize: 12, color: '#666' }}>
                    共包含 {batchUploadResults.collection.total_materials} 个素材（上传 {batchUploadResults.success_count} 个，关联重复 {batchUploadResults.skipped_count || 0} 个）
                  </p>
                </div>
              )}
              
              {batchUploadResults.results && batchUploadResults.results.some(r => r.status === 'failed') && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <p style={{ color: '#ff4d4f', fontWeight: 'bold', margin: 0 }}>失败详情：</p>
                    <Button 
                      type="primary" 
                      danger 
                      size="small" 
                      onClick={handleRetryFailed}
                      loading={batchUploading}
                      disabled={failedFiles.length === 0}
                    >
                      重新上传失败文件 ({failedFiles.length})
                    </Button>
                  </div>
                  {batchUploadResults.results
                    .filter(r => r.status === 'failed')
                    .map((r, i) => (
                      <p key={i} style={{ color: '#ff4d4f', fontSize: 12 }}>
                        {r.filename}: {r.error}
                      </p>
                    ))}
                </div>
              )}
              
              {batchUploadResults.results && batchUploadResults.results.some(r => r.status === 'skipped') && (
                <div style={{ marginTop: 8 }}>
                  <p style={{ color: '#faad14', fontWeight: 'bold' }}>跳过详情：</p>
                  {batchUploadResults.results
                    .filter(r => r.status === 'skipped')
                    .map((r, i) => (
                      <p key={i} style={{ color: '#faad14', fontSize: 12 }}>
                        {r.filename}: {r.reason}
                      </p>
                    ))}
                </div>
              )}
            </div>
          )}
        </Form>
      </Modal>

      {/* 详情模态框 */}
      <Modal
        title="素材详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        width={800}
        footer={null}
      >
        {selectedMaterial && (
          <Tabs defaultActiveKey="info">
            <TabPane tab="基本信息" key="info">
              {isVideoMaterial(selectedMaterial) ? (
                <video
                  src={getImageUrl(selectedMaterial.file_url)}
                  controls
                  style={{ maxWidth: '100%', maxHeight: 300, background: '#111' }}
                />
              ) : (
                <Image
                  src={getImageUrl(selectedMaterial.file_url)}
                  style={{ maxWidth: '100%', maxHeight: 300 }}
                />
              )}
              <p><strong>名称：</strong>{selectedMaterial.name}</p>
              <p><strong>类型：</strong>{isVideoMaterial(selectedMaterial) ? '视频' : '图片'}</p>
              <p><strong>MIME：</strong>{selectedMaterial.mime_type || '-'}</p>
              <p><strong>分类：</strong>{selectedMaterial.category}</p>
              <p><strong>文件大小：</strong>{(selectedMaterial.file_size / 1024).toFixed(2)} KB</p>
              {!isVideoMaterial(selectedMaterial) && (
                <p><strong>尺寸：</strong>{selectedMaterial.width} x {selectedMaterial.height}</p>
              )}
              <p><strong>使用次数：</strong>{selectedMaterial.usage_count}</p>
            </TabPane>
            <TabPane tab="上传记录" key="records">
              <List
                dataSource={uploadRecords}
                renderItem={item => (
                  <List.Item>
                    <List.Item.Meta
                      title={
                        <Space>
                          <span>Facebook 账户 ID: {item.facebook_account_id || item.ad_account_id}</span>
                          <Tag color={item.status === 'success' ? 'green' : 'red'}>
                            {item.status === 'success' ? '成功' : '失败'}
                          </Tag>
                        </Space>
                      }
                      description={
                        <div>
                          <p>Facebook 账户 ID: {item.facebook_account_id || '-'}</p>
                          <p>BM 资产 ID: {item.facebook_asset_id || '-'}</p>
                          <p>Facebook 返回 ID: {item.facebook_asset_hash || '-'}</p>
                          <p>上传开发者账号: {item.developer_account_name || '-'}</p>
                          <p>上传时间: {new Date(item.uploaded_at).toLocaleString()}</p>
                          {item.error_message && (
                            <p style={{ color: 'red' }}>错误: {item.error_message}</p>
                          )}
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            </TabPane>
          </Tabs>
        )}
      </Modal>
    </div>
  );
};

export default Materials;
